/**
 * GATE-1 — THE QUERY-GATE PREDICATE. One law, both shells.
 *
 * This is the semantics NAV-SWEEP-1b established and live-proved on the seven admin surfaces, lifted
 * out of AdminQueryState so the teacher shell consumes the SAME rule rather than a second copy of it.
 * The predicate is the only shared piece: each shell keeps its own presentation (the admin card is
 * neutral/token'd, the teacher path renders StatePanel via queryStateGate).
 *
 * A gate keyed on the obvious pair — isLoading / isError — has TWO holes, and both were found live,
 * never in jsdom:
 *
 *   1. THE BACKOFF GAP. react-query's `isLoading` is `isPending && isFetching`, so BETWEEN a failed
 *      attempt and its retry it goes FALSE while `isError` is still false too. For ~1s per retry the
 *      gate returns "ready" and the caller renders its empty branch from data that never arrived —
 *      a school with no teachers, a teacher with no growth, "a school framework isn't set yet".
 *      Cured by keying loading on isPending, which stays true across the whole pending window.
 *
 *   2. PAUSED. Under react-query's default networkMode:"online", a retry that comes due while the
 *      online-manager believes the connection is down is PAUSED, not attempted: the query sits at
 *      status:"pending" / fetchStatus:"paused" FOREVER and `isError` is NEVER reached. A gate that
 *      waits for isError therefore shows an eternal skeleton in exactly the conditions a reviewer
 *      meets — hotel wifi, a dropped VPN, a backend restart mid-demo.
 *
 * PAUSED IS CHECKED FIRST, AND WITHOUT A GRACE WINDOW (ruling R1). To the reader, "paused" and
 * "failed" are the same fact: this did not load. A grace period's only output would be a longer
 * skeleton before the identical card.
 *
 * Framework-free on purpose — no React, no i18n — so it is trivially testable and cannot drift into
 * presentation. Accepts either a react-query result object or a bag of the same flags, because some
 * callers aggregate several queries into one set of booleans.
 */

export const GATE = {
  ERROR: "error",
  LOADING: "loading",
  READY: "ready",
};

/**
 * @param {{isPending?: boolean, isLoading?: boolean, isError?: boolean, isPaused?: boolean}} q
 *        a react-query result, or the equivalent flags (aggregated across queries is fine).
 * @returns {"error"|"loading"|"ready"}
 */
export function queryGate(q) {
  if (!q) return GATE.READY;

  // Paused counts as failed-to-load, and it is decided first — see (2) above.
  if (q.isError || q.isPaused) return GATE.ERROR;

  // isPending, NOT isLoading — see (1) above. Callers that only have the legacy flag still get the
  // old behaviour rather than a crash; `??` keeps `isPending: false` authoritative.
  if (q.isPending ?? q.isLoading) return GATE.LOADING;

  return GATE.READY;
}

/**
 * Is this failure a NOT-FOUND rather than a fault of ours?
 *
 * DELIBERATELY NOT A FOURTH GATE STATE. ~30 call sites compare `=== GATE.ERROR`, `!== GATE.ERROR` or
 * `!== GATE.READY`. A new state would silently reinterpret all of them: ~20 error branches would
 * stop firing on a 404 and render nothing, and TeacherWorkspacePage's `!== GATE.ERROR` would flip to
 * treating a 404 as "Mira is available". So a 404 stays GATE.ERROR for every DECISION, and only the
 * two presenters differ in what they SHOW.
 *
 * C-146 item 2 is why it exists: a cross-school read now returns 404 with the same body as a true
 * not-found, so a foreign id and a missing one must be indistinguishable on screen. Anything that
 * explained the difference would be the 403-with-details C-140 forbids.
 *
 * Framework-free like the rest of this file. Reads the axios shape and a bare `status` fallback.
 */
export function isNotFoundError(error) {
  if (!error) return false;
  const status = error?.response?.status ?? error?.status;
  return status === 404;
}

export const isGateError = (q) => queryGate(q) === GATE.ERROR;
export const isGateLoading = (q) => queryGate(q) === GATE.LOADING;

export default queryGate;
